import { useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { put } from "@common/api/apiShared";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation";
import { usePane } from "../../../main/panes/paneLayout/usePane";
import { usePanes } from "../../../main/panes/usePanes";
import { useLegalEntityContactsQuery } from "../../useLegalEntityContactsQuery";
import { getAdditionalRecipientOptions } from "../auditAdditionalRecipients.utils";
import { ChangeAdditionalRecipientsPaneProps, ChangeAdditionalRecipientsSaveModel } from "./ChangeAdditionalRecipientsPane.types";

export function useChangeAdditionalRecipientsPane(props: ChangeAdditionalRecipientsPaneProps) {
    const { translateString } = useTranslation();
    const snackbar = useDotLegalSnackbar();
    const { close, setHasUnsavedChanges } = usePane();
    const { refetchPageBehindPanes } = usePanes();
    const [saveModel, setSaveModel] = useState<ChangeAdditionalRecipientsSaveModel>({
        contactIds: props.additionalRecipientIds,
        revokeAccessForRemovedRecipients: false,
    });

    const contactsQuery = useLegalEntityContactsQuery(props.legalEntityId);
    const additionalRecipientOptions = getAdditionalRecipientOptions(contactsQuery.data?.rows ?? [], props.contactId ?? undefined, translateString);
    const hasRemovedAdditionalRecipients = props.additionalRecipientIds.some((id) => !saveModel.contactIds.includes(id));

    const saveMutation = useDotLegalMutation(changeAdditionalRecipientsApi, {
        onSuccess: () => {
            snackbar.show(translateString("additionalRecipientsChanged"));
            refetchPageBehindPanes();
            close();
        },
    });

    function updateSaveModel(changedSaveModel: ChangeAdditionalRecipientsSaveModel) {
        setHasUnsavedChanges(true);
        setSaveModel(changedSaveModel);
    }

    const modelUpdater = {
        onContactIdsChange: (contactIds: Array<string>) => updateSaveModel({ ...saveModel, contactIds }),
        onRevokeAccessForRemovedRecipientsChange: (revokeAccessForRemovedRecipients: boolean) =>
            updateSaveModel({ ...saveModel, revokeAccessForRemovedRecipients }),
    };

    function onSaveClick() {
        saveMutation.mutateAsync();
    }

    function changeAdditionalRecipientsApi() {
        return put(`/legalEntityAudit/${props.auditId}/changeAdditionalRecipients`, saveModel);
    }

    return {
        saveModel,
        modelUpdater,
        additionalRecipientOptions,
        hasRemovedAdditionalRecipients,
        isLoadingContacts: contactsQuery.isLoading,
        onSaveClick,
        isSaving: saveMutation.isLoading,
    };
}
